import React, { useEffect, useCallback, useState, useMemo } from 'react';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  FeedbackQuestion,
  FeedbackQuestionsById,
  FeedbackUpdateAnswerResponse,
  updateAnswer,
} from './api';
import { logError } from 'shared/utils/errorLogs';
import { ErrorText, StaticText, StyledMakeEditsDiv } from './components/Shared';
import {
  FeedbackCommentBodyWithIndex,
  IMPACT_SUMMARY_QUESTION_ID,
  INDICATOR_DETAILS_QUESTION_ID,
  INDICATOR_SUMMARY_QUESTION_ID,
  SelectedFeedback,
} from '../utils';
import { ButtonProps } from 'shared/design-system/components/TrueImpactSimpleModal';
import { MakeEditsQuestion } from './MakeEditsQuestion';
import {
  disabledNextQuestionIds,
  FeedbackQuestionHandler,
  FeedbackSectionInfo,
  isNextQuestionDisabled,
} from './utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import {
  customQuestionsById,
  customQuestionToFeedbackQuestion,
  useCustomQuestions,
} from 'admin/pages/ManageAccounts/CustomQuestions/hook';
import { BENEFICIARY_TERM_QUESTION_ID } from '../../ReportBuilder/Components/SectionMultiRadio';
import { isReportPassed } from '../../ReportBuilder/Components/IndicatorDetails/utils';
import { Report } from 'shared/components/WelcomeBack/utils';

const clearDataQuestionIds: number[] = [110001, 110002, 110003];

const getIgnoreClearData = (
  selectedSection: MakeEditsModalProps['selectedSection'],
  questionId: MakeEditsModalProps['selectedSection']['question']['questionId'],
  previousAnswer: string | undefined,
  newAnswer: string | undefined,
): boolean | undefined => {
  if (previousAnswer === newAnswer) return true;
  if (clearDataQuestionIds.includes(questionId)) return false;
  return (
    disabledNextQuestionIds.includes(questionId) ||
    selectedSection.section.sectionId === 'budget' ||
    selectedSection.section.sectionId === 'activities'
  );
};

const styles = `.modal-wrapper .modal-content { position: relative; background-color: ${COLORS.whiteBase}}`;
const hideFooter = [
  IMPACT_SUMMARY_QUESTION_ID,
  INDICATOR_DETAILS_QUESTION_ID,
  INDICATOR_SUMMARY_QUESTION_ID,
];

export interface MakeEditsModalProps {
  open: boolean;
  isFinalizedUI: boolean;
  report: Report;
  reportId: number;
  reportYear?: string;
  isError: boolean;
  isLoading: boolean;
  programName: string;
  isAnswersLoading: boolean;
  questions: FeedbackQuestionsById;
  feedbackByQuestionId: {
    [questionId: number]: FeedbackCommentBodyWithIndex[];
  };
  feedback: FeedbackCommentBodyWithIndex[];
  selectedSection: SelectedFeedback & {
    question: NonNullable<SelectedFeedback['question']>;
  };
  handleAnswerUpdate: (
    questionId: number,
    answer: string,
    questionText: string,
    questionType: FeedbackQuestion['questionType'],
  ) => unknown;
  onClose: () => unknown;
  reloadAnswers: () => unknown;
  markAsCompleted: () => unknown;
}

export type FeedbackMakeEditsHandler = React.ElementRef<
  React.ForwardRefExoticComponent<
    FeedbackSectionInfo & React.RefAttributes<FeedbackQuestionHandler>
  >
>;

const componentErrorText =
  'Something went wrong, please try refreshing the page.';

const OPTIONAL_QUESTION_IDS: number[] = [110050];
const MakeEditsModal: React.FC<MakeEditsModalProps> = ({
  open,
  report,
  reportYear,
  questions,
  isError,
  feedback,
  isLoading,
  reportId,
  programName,
  isFinalizedUI,
  selectedSection,
  isAnswersLoading,
  feedbackByQuestionId,
  handleAnswerUpdate,
  markAsCompleted,
  reloadAnswers,
  onClose,
}) => {
  const client = useClient();
  const { customQuestions: c, guidanceTextResponse } = useCustomQuestions({
    reportId,
  });
  const question = questions[selectedSection.question.questionId];
  const [requestState, makeRequest] = useGraphQLRequest<
    FeedbackUpdateAnswerResponse
  >();
  const [backQuestions, setBackQuestions] = useState<FeedbackQuestion[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<
    FeedbackQuestion | undefined
  >(question);
  const modalContentRef = React.useRef<HTMLDivElement>(null);
  const ref = React.useRef<FeedbackMakeEditsHandler>(null);
  const [errorText, setErrorText] = useState<string | undefined>(undefined);

  const customQuestions = useMemo(() => customQuestionsById(c), [c]);
  const isCustomQuestion =
    selectedSection.section.sectionId === 'custom_questions';

  const beneficiaryTerm =
    questions[BENEFICIARY_TERM_QUESTION_ID]?.answers[0]?.text;

  // useEffect(() => {
  //   console.log('here');
  //   setSelectedQuestion(s => s || question);
  // }, [question]);

  useEffect(() => {
    setBackQuestions([]);
    const qId = selectedSection.question.questionId;
    const q = isCustomQuestion
      ? customQuestionToFeedbackQuestion(customQuestions[qId]?.question)
      : questions[qId];
    setSelectedQuestion(q);
  }, [
    customQuestions,
    selectedSection.question.questionId,
    setSelectedQuestion,
    setBackQuestions,
  ]);

  useEffect(() => {
    if (requestState.type !== 'REQUEST_SUCCESS') return;
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
    const response =
      requestState.data.newNpoAnswer || requestState.data.updateNpoAnswer;
    setBackQuestions(s => (selectedQuestion ? [...s, selectedQuestion] : s));
    if (!response) {
      setErrorText('Unable to save the details. Please try again!');
      return;
    }
    if (!isFinalizedUI && isNextQuestionDisabled(selectedQuestion?.dbId)) {
      setSelectedQuestion(undefined);
      return;
    }
    if (response?.nextQuestion.dbId === null) {
      const impactDetailsQuestion = questions[INDICATOR_DETAILS_QUESTION_ID];
      if (
        impactDetailsQuestion &&
        selectedQuestion?.dbId === IMPACT_SUMMARY_QUESTION_ID
      ) {
        setSelectedQuestion(impactDetailsQuestion);
        return;
      }
      setSelectedQuestion(undefined);
      return;
    }
    const nextQuestionId = response.nextQuestion.dbId;
    setSelectedQuestion({
      answers:
        response.nextQuestion.answers ||
        questions[nextQuestionId]?.answers ||
        [],
      dbId: nextQuestionId,
      enumResponses: response.nextQuestion.enumResponses,
      questionType: response.nextQuestion.questionType,
      text: response.nextQuestion.text,
    });
  }, [requestState, setSelectedQuestion]);

  const handleNext = useCallback(() => {
    if (!ref.current) {
      setErrorText(componentErrorText);
      logError(
        'Something went wrong with make edits modal in feedback',
        'error',
      );
      return;
    }
    const {
      answer,
      errorText,
      onCompleted,
      onNextClicked,
      questionId,
      reportId,
    } = ref.current;
    const error = errorText();
    const answeredText = answer();

    if (error) {
      setErrorText(error);
      return;
    }
    if (!answeredText && !OPTIONAL_QUESTION_IDS.includes(questionId)) {
      if (onNextClicked) {
        setErrorText(undefined);
        onNextClicked();
        return;
      }
      setErrorText(componentErrorText);
      return;
    }

    if (selectedQuestion) {
      handleAnswerUpdate(
        questionId,
        answeredText,
        selectedQuestion.text,
        selectedQuestion.questionType,
      );
    }
    onCompleted?.(answeredText);
    setErrorText(undefined);
    const id = selectedQuestion?.answers[0]?.dbId || undefined;
    const answerId = selectedQuestion?.enumResponses?.find(
      x => x.response === answeredText,
    )?.dbId;
    makeRequest(
      updateAnswer(client!, {
        id,
        reportId,
        answerId,
        questionId,
        ignoreClearData: getIgnoreClearData(
          selectedSection,
          questionId,
          selectedQuestion?.answers[0]?.text,
          answeredText,
        ),
        answer: answeredText,
      }),
    );
  }, [selectedQuestion, ref.current, setErrorText]);

  const handleBack = useCallback(() => {
    const onBack = ref.current?.onBackClicked;
    if (onBack) {
      onBack();
      return;
    }
    const lastQuestion = backQuestions[backQuestions.length - 1];
    setBackQuestions(s => {
      const newState = [...s];
      newState.pop();
      return newState;
    });
    setSelectedQuestion(lastQuestion);
  }, [backQuestions, setBackQuestions, setSelectedQuestion]);

  const handleCompleted = useCallback(() => {
    onClose();
    markAsCompleted();
  }, [markAsCompleted, onClose]);

  const questionId =
    selectedQuestion?.dbId || selectedSection.question.questionId;
  const questionAnswered =
    selectedQuestion?.answers[0] || questions[questionId]?.answers[0];
  const questionText =
    questionAnswered?.questionText ||
    selectedQuestion?.text ||
    selectedSection.question.questionText;
  const answer = questionAnswered?.text || '';
  const startDate = questionAnswered?.date1 || undefined;
  const endDate = questionAnswered?.date2 || report.endDate || undefined;

  const next: ButtonProps = {
    text: selectedQuestion === undefined ? 'Completed' : 'Save & Continue',
    onClick: selectedQuestion === undefined ? handleCompleted : handleNext,
    isLoading: requestState.type === 'REQUEST_START',
  };
  const previous: ButtonProps = {
    text: 'Back',
    onClick: handleBack,
    disabled:
      requestState.type === 'REQUEST_START' ||
      backQuestions.length < 1 ||
      (selectedSection.section.sectionId === 'beneficiaries' &&
        !ref.current?.onBackClicked),
  };

  const isImpactModel =
    selectedQuestion?.dbId === INDICATOR_SUMMARY_QUESTION_ID;

  const isReportDatePassed =
    report.stage === 'FINAL' ||
    isReportPassed(
      endDate,
      reportYear ? Number(reportYear) : undefined,
      report.stage,
    );

  return (
    <TrueImpactModal
      open={open}
      maxWidth="1000px"
      showExpandButton={true}
      initialExpandValue={true}
      errorText={errorText}
      onClose={onClose}
      addStyles={isImpactModel ? styles : undefined}
      hideHeader={isImpactModel}
      hideFooter={
        isCustomQuestion ||
        (selectedQuestion && hideFooter.includes(selectedQuestion.dbId))
      }
      next={next}
      previous={previous}
      modalContentRef={modalContentRef}
    >
      {isLoading ? (
        <TrueImpactLoading />
      ) : isError ? (
        <ErrorText />
      ) : (
        <StyledMakeEditsDiv>
          <StaticText selectedSection={selectedSection} />
          <MakeEditsQuestion
            report={report}
            isThirdParty={report.cnOptedIn || false}
            question={selectedQuestion}
            questionRef={ref}
            answer={answer}
            reportStage={report.stage}
            reportStatus={report.status}
            reportId={reportId}
            reportYear={reportYear}
            startDate={startDate}
            endDate={endDate}
            feedback={feedback}
            handleClose={onClose}
            programName={programName}
            isReportPassed={isReportDatePassed}
            loadingAnswers={isAnswersLoading}
            feedbackByQuestionId={feedbackByQuestionId}
            reloadAnswers={reloadAnswers}
            handleCompleted={handleCompleted}
            setSelectedQuestion={setSelectedQuestion}
            handleAnswerUpdate={handleAnswerUpdate}
            questions={questions}
            beneficiaryTerm={beneficiaryTerm}
            guidanceText={guidanceTextResponse}
            questionId={questionId}
            questionText={questionText}
            selectedSection={selectedSection}
            customQuestionsById={customQuestions}
          />
        </StyledMakeEditsDiv>
      )}
    </TrueImpactModal>
  );
};

export default MakeEditsModal;
